import {
  AsyncPipe,
  DecimalPipe,
  DOCUMENT,
  NgFor,
  NgIf,
  SlicePipe,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  DotFitter,
  Fitter,
} from '@sc/data-access-models';
import {
  DotFittersSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { LocaleDatePipe } from '@sc/data-access-translation';
import {
  FitterNameDisplayContext,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { callCostLabel, dayjs, ScreenUtils } from '@sc/utils';
import { ButtonComponent } from '../../button';
import { IconComponent } from '../../icon';
import { RatingComponent } from '../../rating';
import { AppointmentCommonTimeLinesComponent } from '../appointment-common-time-lines';
import { incrementDisplayedFittersForDay } from './appointment-earliest-list.utils';

@Component({
  selector: 'sc-appointment-earliest-list',
  styleUrls: ['./appointment-earliest-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    IconComponent,
    RatingComponent,
    ButtonComponent,
    AppointmentCommonTimeLinesComponent,
    PhoneNumberPipe,
    FitterNamePipe,
    MatExpansionModule,
    MatProgressSpinnerModule,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
    DecimalPipe,
    SlicePipe,
  ],
  template: ` <mat-accordion class="group-by-days" [multi]="multiUnfolds">
    <mat-expansion-panel
      *ngFor="
        let appointmentsDay of getAllAvailableDays()
          | slice: 0:numbDisplayedDays;
        let i = index
      "
      [id]="generateDayId(appointmentsDay)"
      [expanded]="isExpandedDay(appointmentsDay, i)"
      (afterExpand)="onAfterExpandDay(appointmentsDay)"
    >
      <mat-expansion-panel-header>
        <mat-panel-title class="sc-design-text-2">
          {{
            ('GlobalApplicativeConstants.textBeforeDate' | translate) !==
            'GlobalApplicativeConstants.textBeforeDate'
              ? ('GlobalApplicativeConstants.textBeforeDate' | translate)
              : ''
          }}
          <h2
            class="appointment-day"
            [style.text-decoration]="change ? 'none' : undefined"
          >
            {{ appointmentsDay | localeDate: 'fullDate' | async }}
          </h2>
        </mat-panel-title>
      </mat-expansion-panel-header>
      <div class="fitters-container">
        <div
          class="fitter"
          [class.has-no-appointment]="
            isNoApmtFitter(fitterId) || isWeatherApmtFitter(fitterId)
          "
          [class.last-with-appointments]="
            isLastWithAppointments(fitterId, appointmentsDay)
          "
          (mouseenter)="onFitterHover(fitterId)"
          (mouseleave)="onFitterHover('')"
          *ngFor="
            let fitterId of getAllAvailableFittersInDay(appointmentsDay)
              | slice: 0:getNumberOfDisplayedFitterByDays(appointmentsDay)
          "
          #fitter_{{fitterId}}
        >
          <ng-container *ngIf="!!fittersById?.get(fitterId)?.id">
            <div class="header-fitter">
              <div class="row">
                <h3 class="fitter-name sc-design-text-3-bold">
                  {{
                    fittersById.get(fitterId)
                      | fitterName: getFitterNameContext(fitterId)
                      | async
                  }}
                  <span
                    class="mobility-no-cost"
                    *ngIf="showFitterNoCostInfo(fitterId)"
                  >
                    {{ 'AppointmentNearestList.mobilityNoCost' | translate }}
                  </span>
                </h3>

                <div class="infos-fitter sc-design-text-4">
                  <div
                    class="info-fitter"
                    *ngIf="shouldDisplayAddress(fitterId)"
                  >
                    <sc-icon
                      name="road"
                      specificName="appointment_earliest_list_road"
                    ></sc-icon>
                    <div class="value">
                      <p>
                        {{
                          'GlobalApplicativeConstants.distance.interventionDistanceKm'
                            | translate
                              : {
                                  distance:
                                    fittersById?.get(fitterId)?.distance >= 0
                                      ? (fittersById?.get(fitterId)?.distance /
                                          1000 | number: '1.1-1')
                                      : '-'
                                }
                        }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="dot sc-conf-display-google-reviews"
                    *ngIf="
                      fittersSB.showGoogleReview() &&
                      fittersSB.isNotItinerant(fittersById.get(fitterId))
                    "
                  ></span>
                  <sc-rating
                    [rating]="fittersById.get(fitterId)?.google?.rating"
                    [brandId]="fittersById.get(fitterId)?.brandId"
                  ></sc-rating>
                </div>
              </div>
              <div class="row" *ngIf="shouldDisplayAddress(fitterId)">
                <div class="sc-design-text-4">
                  <p
                    [innerHtml]="
                      fittersSB.getCompleteAddress(fittersById?.get(fitterId))
                    "
                  ></p>
                </div>
              </div>
              <div
                class="row phone sc-conf-display-fitter-phone sc-design-text-3-bold"
                *ngIf="fittersById?.get(fitterId)?.phone"
              >
                <sc-icon name="phone"></sc-icon>
                <ng-container
                  *ngIf="
                    clientAppId === 'customer' &&
                      featureFlippingService.isFeatureActivated(
                        'b2c-phone-number-click-to-call-in-mobile'
                      ) &&
                      isMobileResolution();
                    else phoneText
                  "
                >
                  <a
                    [href]="
                      'tel:' +
                      (fittersById?.get(fitterId).phone | phoneNumber | async)
                    "
                  >
                    {{
                      fittersById?.get(fitterId).phone | phoneNumber | async
                    }}</a
                  ><span class="callCostLabel">{{
                    callCostLabel(fittersById?.get(fitterId).phone) | translate
                  }}</span>
                </ng-container>
                <ng-template #phoneText>
                  <p>
                    {{ fittersById?.get(fitterId).phone | phoneNumber | async
                    }}<span class="callCostLabel">{{
                      callCostLabel(fittersById?.get(fitterId).phone)
                        | translate
                    }}</span>
                  </p>
                </ng-template>
              </div>
              <div
                class="row"
                *ngIf="
                  fitterId === bestScoredFittterId && highlightBestScoreFitter
                "
              >
                <div class="best-score-fitter">
                  <sc-icon name="like"></sc-icon>
                  {{ 'AppointmentEarliestList.insuranceTitle' | translate }}
                </div>
              </div>
              <div class="row">
                <sc-button
                  *ngIf="displaySelectFitter && shouldDisplayAddress(fitterId)"
                  class="show-fitter-details-button-alt"
                  buttonType="mainWhite"
                  role="button"
                  (click)="onShowDetailsClick(fitterId)"
                >
                  {{ 'AppointmentEarliestList.detailsButtonTitle' | translate }}
                </sc-button>
              </div>
            </div>

            <div class="body-fitter">
              <div
                *ngIf="
                  !isNoApmtFitter(fitterId) && !isWeatherApmtFitter(fitterId)
                "
                class="slots-container"
              >
                <sc-appointment-common-time-lines
                  [appointments]="
                    getAppointmentsForFitterInDay(appointmentsDay, fitterId)
                  "
                  [fitter]="fittersById?.get(fitterId)"
                  [displayGarageAppointments]="displayGarageAppointments"
                  [displayHomeAppointments]="displayHomeAppointments"
                  [displayHomeAppointmentsAsHalfDay]="
                    displayHomeAppointmentsAsHalfDay(fitterId)
                  "
                  [displayReplacementVehicle]="displayReplacementVehicle"
                  [displayOnlyReplacementVehicle]="
                    displayOnlyReplacementVehicle
                  "
                  [selectedAppointment]="selectedAppointment"
                  (slotClickEvent)="onSlotClick($event)"
                >
                  <sc-button
                    *ngIf="
                      displaySelectFitter && shouldDisplayAddress(fitterId)
                    "
                    additionalAction
                    class="show-fitter-details-button"
                    buttonType="mainWhite"
                    role="button"
                    (click)="onShowDetailsClick(fitterId)"
                  >
                    {{
                      'AppointmentEarliestList.detailsButtonTitle' | translate
                    }}
                  </sc-button>
                  <div additionalAction class="spacer"></div>
                </sc-appointment-common-time-lines>
              </div>
              <div *ngIf="isNoApmtFitter(fitterId)" class="no-slots-container">
                <span class="no-slots-hint">{{
                  'AppointmentEarliestList.noDisponibility' | translate
                }}</span>
              </div>
              <div
                *ngIf="isWeatherApmtFitter(fitterId)"
                class="no-slots-container"
              >
                <span class="no-slots-hint">{{
                  'AppointmentEarliestList.subNameCardInfo' | translate
                }}</span>
              </div>
              <div
                *ngIf="displayAppointmentRequest(fitterId)"
                class="appointment-request-container"
              >
                <span class="no-good-appointment-question">{{
                  'AppointmentEarliestList.noGoodAppointmentQuestion'
                    | translate
                }}</span>
                <div
                  class="send-appointment-request-button"
                  tabindex="0"
                  role="button"
                  (click)="onClickSendAppointmentRequest(fitterId)"
                  (keydown.enter)="onClickSendAppointmentRequest(fitterId)"
                  (keydown.space)="onClickSendAppointmentRequest(fitterId)"
                >
                  <sc-icon name="folder"></sc-icon>
                  <span>{{
                    getRequestAppointmentBtnText(fitterId) | translate
                  }}</span>
                </div>
              </div>
            </div>
            <div
              class="expansion-panel-footer"
              *ngIf="
                displaySelectFitter ||
                hasReplacementRdv(appointmentsDay, fitterId)
              "
            >
              <sc-button
                *ngIf="displaySelectFitter"
                class="show-fitter-details-button"
                buttonType="mainWhite"
                role="button"
                (click)="onShowDetailsClick(fitterId)"
              >
                {{ 'AppointmentEarliestList.detailsButtonTitle' | translate }}
              </sc-button>
              <div
                class="replacement-vehicle-info"
                *ngIf="hasReplacementRdv(appointmentsDay, fitterId)"
              >
                <span class="replacement-vehicle-info-icon">
                  <sc-icon
                    name="car"
                    specificName="replacement_vehicle"
                    [forceInitialColor]="true"
                  ></sc-icon>
                </span>
                <span class="replacement-vehicle-info-text">{{
                  'AppointmentEarliestList.replacementVehicleAvailability'
                    | translate
                }}</span>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
      <div
        *ngIf="
          hasMoreFittersToShow ||
          getNumberOfDisplayedFitterByDays(appointmentsDay) <
            getAllAvailableFittersInDay(appointmentsDay).length
        "
        class="show-more-fitters-container"
      >
        <button
          class="show-more-fitters-button sc-design-text-3"
          [title]="
            'AppointmentEarliestList.seeMoreFittersTitle'
              | translate
                : {
                    day: (appointmentsDay | localeDate: 'fullDate' | async),
                    clientAppId: clientAppId
                  }
          "
          (click)="onShowMoreFittersClick(appointmentsDay)"
        >
          <mat-spinner
            *ngIf="loading"
            class="mat-spinner-color"
            [diameter]="20"
          ></mat-spinner>
          <sc-icon
            *ngIf="!loading"
            name="more"
            [specificName]="'earliest_list_more'"
          ></sc-icon>
          <span class="sc-design-text-3-bold">
            <p>
              {{
                'AppointmentEarliestList.seeMoreFittersLabel'
                  | translate: { clientAppId: clientAppId }
              }}
            </p>
          </span>
        </button>
      </div>
    </mat-expansion-panel>

    <button
      class="show-more-days-button"
      [title]="
        'AppointmentEarliestList.seeMoreDaysTitle'
          | translate
            : {
                day:
                  (getAllAvailableDays()[numbDisplayedDays - 1]
                  | localeDate: 'fullDate'
                  | async)
              }
      "
      (click)="onShowMoreDaysClick()"
      *ngIf="numbDisplayedDays < appointmentsByDaysByFitters.size"
    >
      <sc-icon
        name="more"
        [specificName]="
          isMobileResolution()
            ? 'earliest_list_more_mobile'
            : 'earliest_list_more'
        "
      ></sc-icon>
      <span class="sc-design-text-3-bold">
        <p>{{ 'AppointmentEarliestList.seeMoreDaysLabel' | translate }}</p>
      </span>
    </button>
  </mat-accordion>`,
})
export class AppointmentEarliestListComponent implements OnChanges {
  @Input() selectedAppointment: Appointment;
  @Input() numbDisplayedSlots: number;
  numbDisplayedFitters = 3;
  numbDisplayedDays = 3;

  @Input() multiUnfolds = true;
  @Input() unfoldsAll = false;

  @Input() selectedFitterId: string;

  @Input() enabledReplacementVehicleStyle = true;
  @Input() disabledReplacementVehicle = false;
  @Input() replacementVehicleSelection;
  @Input() defaultInterventionDuration: string;
  @Input() displayHomeAppointments = true;
  @Input() displayGarageAppointments = true;
  @Input() displayReplacementVehicle = true;
  @Input() displayOnlyReplacementVehicle = true;
  @Input() displaySelectFitter = true;
  @Input() hasMoreFittersToShow = true;
  @Input() set selectedDay(day) {
    if (day && this.appointmentsByDaysByFitters.size) {
      this._selectedDay = day;
      this.scrollToSelectedDate(day);
    }
  }
  @Input() weatherEventFitters: Fitter[];
  @Input() highlightBestScoreFitter = false;
  @Input() fittersById: Map<string, Fitter>;
  @Input() appointmentsByDaysByFitters: Map<string, Map<string, Appointment[]>>;
  @Input() bestScoredFittterId: string;
  _noAppointmentFitters: Fitter[];
  @Input() set noAppointmentFitters(noAppointmentFitters: Fitter[]) {
    this._noAppointmentFitters = noAppointmentFitters.filter(
      (fitter) =>
        this.configurationService.getBrandSearchVersion(fitter.brandId) !==
        'NO_SLOT'
    );
  }
  @Input() set noDotfittersIDWithOutsideApp(
    noDotfittersIDWithOutsideApp: string[]
  ) {
    this._currentNoDotFittersIDWithOutsideApp = noDotfittersIDWithOutsideApp;
  }
  @Input() loading: boolean;
  @Input() dossier: Dossier;

  @Output() slotClickEvent = new EventEmitter<Appointment>();
  @Output() requestAppointment = new EventEmitter<string>();
  @Output() showMoreClick = new EventEmitter<Fitter>();
  @Output() selectDay = new EventEmitter<Date>();
  @Output() selectFitter = new EventEmitter<Fitter>();
  @Output() showFitterClickEvent = new EventEmitter<{
    brandId: string;
    fitterId: string;
  }>();
  @Output() fitterHoverEvent = new EventEmitter<string>();
  @Output() showMoreFittersEvent = new EventEmitter<void>();

  currentDotFitters: DotFitter[] = [];
  private _currentNoDotFittersIDWithOutsideApp: string[] = [];

  //This variable has been create because the css "text-transform: capitalize !important;"
  //doesn't apply on the <h2> when generate more fitters
  // /!\ Problem only appears on Iphone
  public change = true;

  _selectedDay: string;

  numbDisplayedFittersByDay: {
    day: string;
    numberOfDisplayDays: number;
  }[] = [];

  callCostLabel = callCostLabel;
  constructor(
    private readonly sandboxService: SandboxService,
    public fittersSB: FittersSandboxService,
    public featureFlippingService: FeatureFlippingManagerService,
    public changeDetectorRef: ChangeDetectorRef,
    private readonly configurationService: ConfigurationService,
    public dotFitterSB: DotFittersSandboxService,
    private readonly el: ElementRef<HTMLElement>,
    private readonly focusManager: FocusManagerService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId,
    @Inject(DOCUMENT) private readonly doc: Document
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const appMapChanges = changes.appointmentsByDaysByFitters;

    if (
      appMapChanges &&
      appMapChanges.currentValue !== appMapChanges.previousValue
    ) {
      if (
        !this.appointmentsByDaysByFitters.size &&
        this._noAppointmentFitters.length > 0
      ) {
        this.appointmentsByDaysByFitters.set(
          dayjs().add(1, 'day').format(),
          new Map<string, Appointment[]>()
        );
      }

      // Rebuild numbDisplayedFittersByDay list
      appMapChanges?.currentValue?.forEach((value, key) => {
        if (
          this.numbDisplayedFittersByDay.findIndex(
            (item) => item.day === key
          ) === -1
        ) {
          this.numbDisplayedFittersByDay.push({
            day: key,
            numberOfDisplayDays: this.numbDisplayedFitters,
          });
        }
      });

      this.numbDisplayedFittersByDay.sort(
        (objA, objB) => Date.parse(objA.day) - Date.parse(objB.day)
      );
      this.changeDetectorRef.markForCheck();
    }
  }

  isMobileResolution(): boolean {
    return ScreenUtils.isMobileResolution();
  }

  getNumberOfDisplayedFitterByDays(appointmentsDay) {
    return (
      this.numbDisplayedFittersByDay.find((e) => e.day === appointmentsDay)
        ?.numberOfDisplayDays ?? 0
    );
  }

  hasReplacementRdv(appointmentsDay: string, fitterId: string) {
    const brandId = this.fittersById?.get(fitterId)?.brandId;
    const fitterHasReplacementVehicleFeature =
      this.displayReplacementVehicle &&
      this.featureFlippingService?.isBrandFeatureActivated(
        brandId,
        'can-declare-replacement-vehicle-fitter'
      );
    if (!fitterHasReplacementVehicleFeature) {
      return false;
    } else {
      for (const appointment of this.appointmentsByDaysByFitters
        ?.get(appointmentsDay)
        ?.get(fitterId) || []) {
        if (appointment?.replacementVehicle) return true;
      }
      return false;
    }
  }

  generateDayId(day: string) {
    return `day_${dayjs(day).utc().format('YYYY-MM-DD')}`;
  }

  getAllAvailableDays() {
    return Array.from(this.appointmentsByDaysByFitters?.keys() || []);
  }

  getAllAvailableFitters(): string[] {
    return Array.from(
      new Set(
        this.getAllAvailableDays()
          .slice(0, this.numbDisplayedDays)
          .flatMap((day) => this.getAllAvailableFittersInDay(day))
      )
    );
  }

  getAllAvailableFittersInDay(day: string) {
    return Array.from(this.appointmentsByDaysByFitters?.get(day)?.keys() || [])
      .concat(this._noAppointmentFitters.map((fitter) => fitter.id))
      .concat(this.weatherEventFitters.map((fitter) => fitter.id));
  }

  getAppointmentsForFitterInDay(day: string, fitterId: string) {
    return Array.from(
      this.appointmentsByDaysByFitters?.get(day)?.get(fitterId)?.values() || []
    );
  }

  displayHomeAppointmentsAsHalfDay(fitterId: string) {
    return this.featureFlippingService.isBrandFeatureActivated(
      this.fittersById.get(fitterId).brandId,
      'display-PAD-as-half-day'
    );
  }

  isNoApmtFitter(fitterId: string) {
    return !!this._noAppointmentFitters.some((f) => f.id === fitterId);
  }

  isWeatherApmtFitter(fitterId: string) {
    return !!this.weatherEventFitters.some((f) => f.id === fitterId);
  }

  isLastWithAppointments(fitterId: string, appointmentsDay: string) {
    const allFitters = this.getAllAvailableFittersInDay(appointmentsDay);
    const fittersWithApmt = allFitters.filter(
      (f) => !this.isNoApmtFitter(f) && !this.isWeatherApmtFitter(f)
    );

    return fittersWithApmt[fittersWithApmt.length - 1] === fitterId;
  }

  displayAppointmentRequest(fitterId: string) {
    const fitterBrandId = this.fittersById.get(fitterId)?.brandId;

    const isAlreadyRequested =
      !this.dossier?.appointment &&
      (this.dossier?.requestAppointment?.status === 'REQUESTED' ||
        this.dossier?.requestAppointment?.status === 'MODIFIED') &&
      this.dossier?.requestAppointment?.fitterId === fitterId;

    return (
      this.sandboxService.canDoRequestAppointment(fitterBrandId) &&
      !isAlreadyRequested
    );
  }

  scrollToSelectedDate(day) {
    const date = new Date(day);

    const id = this.generateDayId(date.toISOString());
    this.onShowMoreDaysClick(date.toISOString());

    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });

        (element.firstElementChild as HTMLElement)?.focus();
      }
    }, 1000);
  }

  onShowMoreDaysClick(dayToDisplay?) {
    const numbDays = this.appointmentsByDaysByFitters.size;
    const numbHiddenDays = numbDays - this.numbDisplayedDays;

    const oldNbDisplayedDays = this.numbDisplayedDays;

    if (dayToDisplay) {
      const index = Array.from(this.appointmentsByDaysByFitters.keys()).indexOf(
        dayToDisplay
      );
      if (index > this.numbDisplayedDays) {
        this.numbDisplayedDays = index + 1;
        this.change != this.change;
      }
    } else {
      this.numbDisplayedDays =
        numbHiddenDays - 2 > 0 ? this.numbDisplayedDays + 2 : numbDays;
      this.change != this.change;
    }

    if (oldNbDisplayedDays < this.numbDisplayedDays) {
      const dayAccordionId = this.generateDayId(
        this.getAllAvailableDays()?.[oldNbDisplayedDays]
      );

      if (dayAccordionId) {
        setTimeout(() => {
          const newDayEl = this.doc.getElementById(dayAccordionId);

          if (newDayEl) {
            this.focusManager.focusFirstElementAllowed(newDayEl, true);
            this.changeDetectorRef.markForCheck();
          }
        }, 100);
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  onShowMoreFittersClick(appointmentsDay: string) {
    incrementDisplayedFittersForDay(
      this.numbDisplayedFittersByDay,
      appointmentsDay
    );
    this.showMoreFittersEvent.emit();
  }

  onAfterExpandDay(appointmentsDay: string) {
    setTimeout(() => {
      const element = document.getElementById(
        this.generateDayId(appointmentsDay)
      );
      this._selectedDay = appointmentsDay;
      if (
        !!this.selectedAppointment &&
        !dayjs(this.selectedAppointment.time).isSame(this._selectedDay, 'D')
      )
        this.selectedAppointment = undefined;

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      this.changeDetectorRef.markForCheck();
    }, 100);
  }

  onSlotClick(appointment: Appointment) {
    this.selectedAppointment = appointment;
    this.slotClickEvent.emit(this.selectedAppointment);
  }

  isExpandedDay(appointmentsDay: string, i: number) {
    const { unfoldsAll, _selectedDay, selectedAppointment } = this;
    const selectedDayFormated = _selectedDay ? new Date(_selectedDay) : null;
    selectedDayFormated?.setHours(0, 0, 0, 0);

    const selectedAppointmentDayFormated = selectedAppointment
      ? new Date(selectedAppointment?.time)
      : null;
    selectedAppointmentDayFormated?.setHours(0, 0, 0, 0);

    return (
      unfoldsAll ||
      selectedDayFormated?.toISOString() === appointmentsDay ||
      selectedAppointmentDayFormated?.toISOString() === appointmentsDay ||
      (i === 0 && !_selectedDay && !selectedAppointment)
    );
  }

  onShowDetailsClick(fitterId: string) {
    const brandId = this.fittersById?.get(fitterId)?.brandId;
    this.showFitterClickEvent.emit({ brandId: brandId, fitterId: fitterId });
  }

  onFitterHover($event) {
    if (this.displayGarageAppointments) {
      this.fitterHoverEvent.emit($event);
    }
  }

  isWeatherEventFitter(fitterId: string) {
    return this.weatherEventFitters.some((f) => f.id === fitterId);
  }

  onClickSendAppointmentRequest(fitterId: string) {
    this.requestAppointment.emit(fitterId);
  }

  shouldDisplayAddress(fitterId: string) {
    return (
      this.fittersSB.isNotItinerant(this.fittersById.get(fitterId)) &&
      this._currentNoDotFittersIDWithOutsideApp.indexOf(fitterId) == -1
    );
  }

  getFitterNameContext(fitterId: string) {
    if (!this.shouldDisplayAddress(fitterId)) {
      return FitterNameDisplayContext.SlotListOnlyMobility;
    } else {
      return undefined;
    }
  }

  isFitterRequestOnlyToCustomer(fitterId: string) {
    const brandId = this.fittersById.get(fitterId)?.brandId;
    return this.featureFlippingService.sendRequestNotificationOnlyToCustomer(
      brandId
    );
  }

  getOnlyCustomerKey(appointmentsFitter: string) {
    return this.isFitterRequestOnlyToCustomer(appointmentsFitter)
      ? 'onlyCustomer'
      : 'all';
  }

  getRequestAppointmentBtnText(appointmentsFitter: string) {
    return `AppointmentEarliestList.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey(appointmentsFitter)}.apmtRequestBtn`;
  }

  showFitterNoCostInfo(fitterId: string) {
    const brandId = this.fittersById.get(fitterId)?.brandId;
    return (
      !this.shouldDisplayAddress(fitterId) &&
      this.featureFlippingService.isBrandFeatureActivated(
        brandId,
        'show-mobility-no-cost-info'
      )
    );
  }
}
